import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AtSign, Mail, Lock, ArrowRight, Eye, EyeOff, MapPin, Icon } from '../../components/ui/Icon';

const API_URL = import.meta.env.VITE_API_URL as string;
import { authApi } from '../../api/auth.api';
import { invitationsApi, type InvitationPreview } from '../../api/invitations.api';
import { useAuthStore } from '../../stores/auth.store';
import { CountrySelect } from '../../components/ui/CountrySelect';
import { guessCountryFromLocale } from '../../utils/countries';
import Spinner from '../../components/ui/Spinner';
import { AppleSignInButton } from '../../components/auth/AppleSignInButton';
import { useTranslation } from 'react-i18next';
import { toast } from '../../components/ui/Toast';
import { GromlyWordmark } from '../../components/brand/GromlyMark';

export default function RegisterPage() {
  const [form, setForm] = useState({ email: '', username: '', password: '' });
  const [nationalityCode, setNationalityCode] = useState(() => guessCountryFromLocale());
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const searchParams  = new URLSearchParams(location.search);
  const redirectParam = searchParams.get('redirect');
  const invitationToken = searchParams.get('token') ?? undefined;
  const redirectTo = (redirectParam && redirectParam.startsWith('/')) ? redirectParam : '/dashboard';
  const loginHref = (() => {
    const qs = new URLSearchParams();
    if (redirectParam) qs.set('redirect', redirectParam);
    return qs.toString() ? `/login?${qs.toString()}` : '/login';
  })();

  const [invitePreview, setInvitePreview] = useState<InvitationPreview | null>(null);

  useEffect(() => {
    if (!invitationToken) return;
    invitationsApi.preview(invitationToken).then(setInvitePreview).catch(() => { /* token may be expired — silent */ });
  }, [invitationToken]);

  function update(field: string, value: string) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!acceptedTerms) {
      setTermsError(true);
      return;
    }
    setTermsError(false);
    setLoading(true);
    try {
      const { accessToken, user, acceptedTripId } = await authApi.register({
        ...form,
        nationalityCode,
        acceptedTerms: true,
        invitationToken,
        locale: i18n.language,
      });
      setAuth(user, accessToken);
      if (acceptedTripId) {
        toast(t('auth.register.toastJoined'), 'success');
        navigate(`/trips/${acceptedTripId}/itinerary`, { replace: true });
      } else {
        if (invitationToken) {
          toast(t('auth.register.toastInviteExpired'), 'info');
        } else {
          toast(t('auth.register.toastVerifyEmail'), 'success');
        }
        navigate(redirectTo, { replace: true });
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || t('auth.register.errorDefault'));
    } finally {
      setLoading(false);
    }
  }

  const inputWrap: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 10,
    border: '1px solid #d6dae2', borderRadius: 10, height: 54,
    background: '#fff', padding: '0 14px', transition: 'border-color 0.15s, box-shadow 0.15s',
  };

  function onFocus(e: React.FocusEvent<HTMLDivElement>) {
    e.currentTarget.style.borderColor = '#0b1420';
    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(11,20,32,0.06)';
  }
  function onBlur(e: React.FocusEvent<HTMLDivElement>) {
    e.currentTarget.style.borderColor = '#d6dae2';
    e.currentTarget.style.boxShadow = 'none';
  }

  const inputStyle: React.CSSProperties = {
    flex: 1, outline: 'none', background: 'transparent', fontSize: 14,
    fontFamily: 'Inter, sans-serif', color: '#0b1420', height: '100%', border: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontFamily: 'monospace', fontSize: 10,
    letterSpacing: '1.6px', textTransform: 'uppercase', marginBottom: 8, color: '#6b7689',
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">

      {/* ── LEFT: editorial ink panel ─────────────────────────────────────── */}
      <div
        className="hidden lg:flex flex-col justify-between px-14 py-11 relative overflow-hidden"
        style={{ background: '#0b1420', color: '#faf7f0' }}
      >
        {/* Dot grid */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(rgba(217,179,130,0.08) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 30% 40%, #000 30%, transparent 70%)',
        }} />
        {/* Glow */}
        <div className="absolute -right-24 -bottom-16 w-96 h-96 rounded-full pointer-events-none" style={{
          background: 'radial-gradient(circle at center, rgba(217,179,130,0.18), transparent 60%)',
          filter: 'blur(30px)',
        }} />

        {/* Top bar */}
        <div className="relative z-10 flex items-center justify-between">
          <GromlyWordmark size={38} dark />
          <div className="flex items-center gap-2.5 font-mono text-[10px] tracking-[1.6px] uppercase" style={{ color: 'rgba(250,247,240,0.5)' }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#d9b382' }} />
            Travel Co. · est. MMXXVI
          </div>
        </div>

        {/* Hero */}
        <div className="relative z-10 max-w-lg">
          <div className="flex items-center gap-2.5 font-mono text-[10.5px] tracking-[2px] uppercase mb-7" style={{ color: '#d9b382' }}>
            <span className="w-9 h-px" style={{ background: '#d9b382', opacity: 0.6 }} />
            {t('auth.register.heroEyebrow')}
          </div>
          <h1 className="font-serif font-light italic leading-none tracking-tight mb-5" style={{ fontSize: 'clamp(52px, 6.5vw, 82px)' }}>
            <span style={{ color: '#faf7f0', fontStyle: 'normal' }}>{t('auth.register.heroLine1a')}</span><span style={{ color: '#d9b382', fontStyle: 'italic' }}>{t('auth.register.heroLine1b')}</span><br />
            <span style={{ color: '#faf7f0', fontStyle: 'normal' }}>{t('auth.register.heroLine2a')}</span><span style={{ color: '#d9b382', fontStyle: 'italic' }}>{t('auth.register.heroLine2b')}</span>
          </h1>
          <p className="font-sans text-base leading-relaxed mb-10" style={{ color: 'rgba(250,247,240,0.7)', lineHeight: 1.75 }}>
            {t('auth.register.heroSubheading')}
          </p>

          {/* Feature list */}
          <div className="space-y-3.5">
            {(t('auth.register.heroFeatures', { returnObjects: true }) as string[]).map(f => (
              <div key={f} className="flex items-center gap-3" style={{ color: 'rgba(250,247,240,0.75)' }}>
                <span className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 text-[9px] font-bold" style={{ background: 'rgba(217,179,130,0.2)', border: '1px solid rgba(217,179,130,0.4)', color: '#d9b382' }}>✓</span>
                <span className="text-sm font-normal" style={{ color: 'rgba(250,247,240,0.75)' }}>{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Ticker */}
        <div className="relative z-10 flex items-center justify-between gap-4 pt-5" style={{ borderTop: '1px solid rgba(250,247,240,0.12)' }}>
          <p className="font-mono text-[10.5px] tracking-[1.6px] uppercase whitespace-nowrap" style={{ color: 'rgba(250,247,240,0.45)' }}>
            <span style={{ color: '#d9b382', fontWeight: 500 }}>Lisboa</span>
            <span className="mx-2.5" style={{ color: 'rgba(250,247,240,0.2)' }}>/</span>
            Hanoi
            <span className="mx-2.5" style={{ color: 'rgba(250,247,240,0.2)' }}>/</span>
            Cape Town
            <span className="mx-2.5" style={{ color: 'rgba(250,247,240,0.2)' }}>/</span>
            Cusco
            <span className="mx-2.5" style={{ color: 'rgba(250,247,240,0.2)' }}>/</span>
            Kyoto
            <span className="mx-2.5" style={{ color: 'rgba(250,247,240,0.2)' }}>/</span>
            <span style={{ color: '#d9b382', fontWeight: 500 }}>Barcelona</span>
          </p>
          <span className="font-mono text-[10px] tracking-[1.4px] uppercase whitespace-nowrap" style={{ color: 'rgba(250,247,240,0.3)' }}>v2.4 · 2026</span>
        </div>
      </div>

      {/* ── RIGHT: form panel ─────────────────────────────────────────────── */}
      <div
        className="flex flex-col px-8 sm:px-14 py-11 relative overflow-hidden"
        style={{ background: '#faf7f0' }}
      >
        {/* Subtle grid */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'linear-gradient(rgba(11,20,32,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(11,20,32,0.025) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }} />

        {/* Top */}
        <div className="relative z-10 flex items-center justify-between">
          <span className="font-mono text-[10px] tracking-[1.6px] uppercase" style={{ color: '#6b7689' }}>
            {t('auth.register.breadcrumb')}
          </span>
          <span className="text-sm" style={{ color: '#6b7689' }}>
            {t('auth.register.alreadyMember')}{' '}
            <Link to={loginHref} className="font-sans font-medium" style={{ color: '#0b1420', borderBottom: '1px solid #0b1420', paddingBottom: 1 }}>
              {t('auth.register.signInLink')}
            </Link>
          </span>
        </div>

        {/* Mobile logo */}
        <div className="lg:hidden relative z-10 flex items-center gap-2 mt-8 mb-2">
          <GromlyWordmark size={26} />
        </div>

        {/* Form centered */}
        <div className="flex-1 flex items-center justify-center relative z-10">
          <div className="w-full max-w-sm">

            {/* Form head */}
            <div className="mb-7">
              <div className="flex items-center gap-2.5 font-mono text-[10.5px] tracking-[2px] uppercase mb-4" style={{ color: '#8a5a2b' }}>
                <span className="w-5 h-5 rounded-full border flex items-center justify-center text-[10px]" style={{ borderColor: '#d9b382', color: '#8a5a2b' }}>01</span>
                {invitePreview ? t('auth.register.tripInviteHeader') : t('auth.register.newAccountHeader')}
              </div>
              <h2 className="font-sans font-light leading-none tracking-tight mb-3" style={{ fontSize: 38, letterSpacing: -0.5, color: '#0b1420' }}>
                {t('auth.register.startAdventure')}
              </h2>
              <p className="font-sans font-medium leading-relaxed" style={{ color: '#3a4558', fontSize: 15, lineHeight: 1.65 }}>
                {t('auth.register.createSubtitle')}
              </p>
            </div>

            {invitePreview && (
              <div className="mb-5 p-4 rounded-xl flex items-start gap-3" style={{ background: '#fef9ef', border: '1px solid #f0d8a8' }}>
                <Icon as={MapPin} size={16} className="mt-0.5 shrink-0" style={{ color: '#8a5a2b' }} />
                <div className="flex-1 min-w-0 text-[13px] leading-relaxed" style={{ color: '#5c3a0a' }}>
                  {t('auth.register.invitePreview', { tripName: invitePreview.tripName, inviterName: invitePreview.inviterName })}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3.5">

              {/* Nickname */}
              <div>
                <label style={labelStyle}>{t('auth.register.username')}</label>
                <div style={inputWrap} onFocusCapture={onFocus} onBlurCapture={onBlur}>
                  <Icon as={AtSign} size={16} style={{ color: '#6b7689', flexShrink: 0 }} />
                  <input
                    type="text" style={inputStyle}
                    placeholder="your-nickname"
                    value={form.username}
                    onChange={e => update('username', e.target.value)}
                    required minLength={3}
                    autoComplete="username"
                  />
                </div>
              </div>

              {/* Nationality — drives personalised entry requirements (visa/vaccines) */}
              <div>
                <label style={labelStyle}>{t('auth.register.nationality')}</label>
                <CountrySelect value={nationalityCode} onChange={setNationalityCode} />
              </div>

              {/* Email */}
              <div>
                <label style={labelStyle}>{t('auth.register.email')}</label>
                <div style={inputWrap} onFocusCapture={onFocus} onBlurCapture={onBlur}>
                  <Icon as={Mail} size={16} style={{ color: '#6b7689', flexShrink: 0 }} />
                  <input
                    type="email" style={inputStyle}
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={e => update('email', e.target.value)}
                    required autoComplete="email"
                  />
                  <span className="font-mono text-[9.5px] tracking-[1.6px] uppercase px-1.5 py-0.5 rounded" style={{ color: '#6b7689', border: '1px solid #d6dae2', background: '#f4f1ea', letterSpacing: '1.6px' }}>@</span>
                </div>
              </div>

              {/* Password */}
              <div>
                <label style={labelStyle}>{t('auth.register.password')}</label>
                <div style={inputWrap} onFocusCapture={onFocus} onBlurCapture={onBlur}>
                  <Icon as={Lock} size={16} style={{ color: '#6b7689', flexShrink: 0 }} />
                  <input
                    type={showPassword ? 'text' : 'password'} style={inputStyle}
                    placeholder={t('auth.register.passwordPlaceholder')}
                    value={form.password}
                    onChange={e => update('password', e.target.value)}
                    required minLength={8}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    className="shrink-0 p-1.5 rounded hover:bg-slate-100 transition-colors cursor-pointer"
                    style={{ color: '#6b7689' }}
                    aria-label={showPassword ? t('auth.register.hidePassword') : t('auth.register.showPassword')}
                    tabIndex={-1}
                  >
                    <Icon as={showPassword ? EyeOff : Eye} size={16} />
                  </button>
                </div>
              </div>

              {/* Terms & Privacy consent */}
              <div>
                <label
                  className={`flex items-start gap-3 cursor-pointer select-none p-2 -m-2 rounded-lg transition-colors ${termsError ? 'ring-1 ring-red-300 bg-red-50/50' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={e => { setAcceptedTerms(e.target.checked); if (e.target.checked) setTermsError(false); }}
                    className={`mt-0.5 h-4 w-4 rounded accent-slate-800 shrink-0 ${termsError ? 'border-red-400' : 'border-slate-300'}`}
                  />
                  <span className="text-xs leading-relaxed" style={{ color: termsError ? '#b91c1c' : '#6b7689' }}>
                    {t('auth.register.acceptStart')}{' '}
                    <Link to="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#0b1420', borderBottom: '1px solid #0b1420', paddingBottom: 1 }}>
                      {t('auth.register.terms')}
                    </Link>
                    {' '}{t('auth.register.and')}{' '}
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#0b1420', borderBottom: '1px solid #0b1420', paddingBottom: 1 }}>
                      {t('auth.register.privacy')}
                    </Link>
                    .
                  </span>
                </label>
                {termsError && (
                  <p className="text-xs text-red-600 mt-1.5 ml-7">
                    {t('auth.register.mustAcceptTerms')}
                  </p>
                )}
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">⚠ {error}</p>
              )}

              {/* OR divider */}
              <div className="flex items-center gap-3 font-mono text-[10px] tracking-[2px] uppercase" style={{ color: '#6b7689' }}>
                <span className="flex-1 h-px" style={{ background: '#d6dae2' }} />
                {t('auth.register.or')}
                <span className="flex-1 h-px" style={{ background: '#d6dae2' }} />
              </div>

              <AppleSignInButton
                invitationToken={invitationToken}
                onClick={() => { if (redirectParam) sessionStorage.setItem('auth-redirect', redirectParam); }}
              />

              {/* Google */}
              <a
                href={`${API_URL}/auth/google${invitationToken ? `?invitationToken=${encodeURIComponent(invitationToken)}` : ''}`}
                onClick={() => { if (redirectParam) sessionStorage.setItem('auth-redirect', redirectParam); }}
                className="flex items-center justify-center gap-2.5 text-sm font-medium transition-all no-underline"
                style={{ height: 48, background: '#fff', border: '1px solid #d6dae2', borderRadius: 10, color: '#0b1420', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#0b1420'; e.currentTarget.style.background = '#f4f1ea'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#d6dae2'; e.currentTarget.style.background = '#fff'; }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/></svg>
                {t('auth.login.continueWithGoogle')}
              </a>

              <div className="flex items-center gap-3 font-mono text-[10px] tracking-[2px] uppercase" style={{ color: '#6b7689' }}>
                <span className="flex-1 h-px" style={{ background: '#d6dae2' }} />
                {t('auth.register.orWithEmail')}
                <span className="flex-1 h-px" style={{ background: '#d6dae2' }} />
              </div>

              {/* CTA */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 font-sans font-medium text-lg transition-all"
                style={{ marginTop: 6, height: 56, background: '#0b1420', color: '#faf7f0', borderRadius: 10, border: 'none', cursor: loading ? 'not-allowed' : 'pointer' }}
                onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = '#000'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 10px 24px -10px rgba(11,20,32,0.45)'; }}}
                onMouseLeave={e => { e.currentTarget.style.background = '#0b1420'; e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
              >
                {loading ? <Spinner size="sm" /> : (
                  <>
                    {t('auth.register.submit')}
                    <span className="w-7 h-7 rounded-full flex items-center justify-center not-italic" style={{ background: '#d9b382', color: '#0b1420' }}>
                      <Icon as={ArrowRight} size={13} />
                    </span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 flex items-center justify-between font-mono text-[10px] tracking-[1.4px] uppercase whitespace-nowrap gap-3" style={{ color: '#6b7689' }}>
          <div className="flex items-center gap-1">
            <Link to="/privacy" className="transition-colors" style={{ color: '#3a4558', textDecoration: 'none' }}>{t('auth.login.footer.privacy')}</Link>
            <span style={{ opacity: 0.4 }}> · </span>
            <Link to="/terms" className="transition-colors" style={{ color: '#3a4558', textDecoration: 'none' }}>{t('auth.login.footer.terms')}</Link>
            <span style={{ opacity: 0.4 }}> · </span>
            <a href="#" className="transition-colors" style={{ color: '#3a4558', textDecoration: 'none' }}>{t('auth.login.footer.status')}</a>
          </div>
          <div>{t('auth.login.footer.copyright')}</div>
        </div>
      </div>
    </div>
  );
}
